import React, { useState, useEffect } from 'react';
import './css/StartMining.css';

function StartMining() {
  const [btcAddress, setBtcAddress] = useState('');
  const [isValidAddress, setIsValidAddress] = useState(false);
  const [miningStatus, setMiningStatus] = useState(null);
  const [isMining, setIsMining] = useState(false);
  const [stats, setStats] = useState({
    hashRate: '0 TH/s',
    uptime: '0 hrs',
  });
  const [miningTime, setMiningTime] = useState('1'); // in hours
  const [miningLog, setMiningLog] = useState([]);

  // Validate BTC address (basic)
  const validateAddress = (address) => {
    const regex = /^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,39}$/;
    return regex.test(address.trim());
  };

  const handleAddressChange = (e) => {
    const val = e.target.value;
    setBtcAddress(val);
    setIsValidAddress(validateAddress(val));
  };

  const handleTimeChange = (e) => {
    setMiningTime(e.target.value);
  };

  // Simulate mining stats update every 10 sec (placeholder)
  useEffect(() => {
    if (!isMining) return;

    const interval = setInterval(() => {
      setStats((prev) => {
        const newUptime = Math.min(parseInt(prev.uptime) + 1, 9999);
        const newHashRate = (Math.random() * 10 + 50).toFixed(2) + ' TH/s';

        // Log a mining event
        setMiningLog((log) => [
          { time: new Date().toLocaleTimeString(), hashRate: newHashRate },
          ...log.slice(0, 9), // Keep last 10 entries
        ]);

        return {
          ...prev,
          uptime: `${newUptime} hrs`,
          hashRate: newHashRate,
        };
      });
    }, 10000);

    return () => clearInterval(interval);
  }, [isMining]);

  const startMining = () => {
    if (!isValidAddress) {
      setMiningStatus({
        type: 'error',
        message: 'Please enter a valid Bitcoin address before starting.',
      });
      return;
    }

    setMiningStatus({
      type: 'success',
      message: `Mining started for address ${btcAddress} for ${miningTime} hour(s).`,
    });
    setIsMining(true);
  };

  const stopMining = () => {
    setIsMining(false);
    setMiningStatus({
      type: 'info',
      message: 'Mining stopped.',
    });
    setStats({ hashRate: '0 TH/s', uptime: '0 hrs' });
    setMiningLog([]);
  };

  return (
    <div className="start-mining dashboard">
      <h2>VyralMining User Dashboard</h2>

      <div className="dashboard-content">
        {/* Left column: input and controls */}
        <section className="input-section">
          <div className="input-group">
            <label htmlFor="btcAddress">Bitcoin Wallet Address</label>
            <input
              type="text"
              id="btcAddress"
              placeholder="Enter your BTC address"
              value={btcAddress}
              onChange={handleAddressChange}
              disabled={isMining}
              autoComplete="off"
            />
            {!isValidAddress && btcAddress.length > 0 && (
              <p className="error-text">Invalid Bitcoin address format.</p>
            )}
          </div>

          <div className="input-group">
            <label htmlFor="miningTime">Select Mining Duration</label>
            <select
              id="miningTime"
              value={miningTime}
              onChange={handleTimeChange}
              disabled={isMining}
            >
              <option value="1">1 Hour</option>
              <option value="2">2 Hours</option>
              <option value="4">4 Hours</option>
              <option value="8">8 Hours</option>
              <option value="12">12 Hours</option>
              <option value="24">24 Hours</option>
            </select>
          </div>

          <div className="button-group">
            {!isMining ? (
              <button
                className="start-button"
                onClick={startMining}
                disabled={!isValidAddress}
              >
                Start Mining
              </button>
            ) : (
              <button className="stop-button" onClick={stopMining}>
                Stop Mining
              </button>
            )}
          </div>
        </section>

        {/* Right column: stats only */}
        <section className="right-column">
          {miningStatus && (
            <div className={`status-message ${miningStatus.type}`}>
              {miningStatus.message}
            </div>
          )}

          <section className="stats-section">
            <h3>Mining Stats</h3>
            <ul className="stats-list">
              <li><strong>Hash Rate:</strong> {stats.hashRate}</li>
              <li><strong>Uptime:</strong> {stats.uptime}</li>
              <li><strong>Mining Duration:</strong> {miningTime} hour(s)</li>
            </ul>
          </section>
        </section>
      </div>

      {/* Full width mining log below */}
      <section className="mining-log-fullwidth">
        <h3>Mining Log (Latest 10)</h3>
        {miningLog.length === 0 ? (
          <p>No log entries yet.</p>
        ) : (
          <ul className="log-list">
            {miningLog.map((entry, i) => (
              <li key={i}>
                <span className="log-time">{entry.time}</span> —{' '}
                <span className="log-hashrate">{entry.hashRate}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default StartMining;